import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map, catchError, throwError, of, forkJoin, switchMap } from 'rxjs';
import {
  ComplianceRecord,
  FullComplianceRecord,
  PaginationOfComplianceRecordsResponse,
  AlcolockDisplayModel
} from '../models/alcolock.model';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AlcolockService {
  private apiUrl = this.getApiUrl();
  private fullDetailsCache = new Map<string, FullComplianceRecord>();

  constructor(private http: HttpClient) {}

  //Proxy workaround for localhost

  private getApiUrl(): string {
    if (environment.alcolockApiBaseUrl.startsWith('/api/')) {
      return environment.alcolockApiBaseUrl;
    }

    return `${environment.alcolockApiBaseUrl}/api/v${environment.alcolockApiVersion}`;
  }

  //REST API calls


  getComplianceById(id: string): Observable<FullComplianceRecord> {
    const fullUrl = `${this.apiUrl}/compliances/${id}/full`;

    return this.http.get<FullComplianceRecord>(fullUrl).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

   getTestResultsInTimeRange(hours: number = 24, passed?: boolean): Observable<AlcolockDisplayModel[]> {
     const take = Math.max(50, hours * 2);

     let params = new HttpParams()
       .set('index', '0')
       .set('take', take.toString());

     if (passed !== undefined) {
       params = params.set('passed', passed.toString());
     }

     const compliancesUrl = `${this.apiUrl}/compliances`;

     return this.http.get<PaginationOfComplianceRecordsResponse>(compliancesUrl, { params }).pipe(
       map(response => {
         if (!response || !response.items || response.items.length === 0) {
           return [];
         }

         const allRecords: ComplianceRecord[] = [];
         response.items.forEach(item => {
           if (Array.isArray(item)) {
             allRecords.push(...item);
           } else if (item && typeof item === 'object' && 'records' in item) {
             allRecords.push(...(item as any).records || []);
           } else {
             allRecords.push(item as ComplianceRecord);
           }
         });

         const displayModels = this.convertBasicRecordsToDisplayModels(allRecords);
         if (displayModels.length > 0) {
            displayModels.slice(0, 3).map(m => ({
              id: m.id,
              timeStamp: m.timeStamp,
              formatted: this.formatTimestamp(m.timeStamp)
            })
          );
        }
         return this.filterByTimeRange(displayModels, hours);
       }),
       catchError(error => {
         console.error('Error in getTestResultsInTimeRange:', error);
         return of([]);
       })
     );
   }


  //Cache management

  clearCache(): void {
    this.fullDetailsCache.clear();
  }


  //Helper methods

  private enrichModel(model: AlcolockDisplayModel, user: any): AlcolockDisplayModel {
    return {
      ...model,
      firstName: user.firstName,
      lastName: user.lastName,
      employeeName: user.firstName && user.lastName
        ? `${user.firstName} ${user.lastName}`
        : user.employeeId || 'Unknown Employee',
      email: user.email,
      isTrainDriver: user.isTrainDriver,
      department: user.department
    };
  }

  formatTimestamp(timestamp: string): string {
    return new Date(timestamp).toLocaleString('sv-SE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }


  calculateStatistics(results: AlcolockDisplayModel[]): { passedCount: number, failedCount: number, totalCount: number } {
    const passedCount = results.filter(r => r.displayResult === 'pass').length;
    const failedCount = results.filter(r => r.displayResult === 'fail').length;
    const totalCount = results.length;

    return { passedCount, failedCount, totalCount };
  }


   private convertBasicRecordsToDisplayModels(records: ComplianceRecord[]): AlcolockDisplayModel[] {
     if (!Array.isArray(records)) {
       console.warn('Invalid records structure:', records);
       return [];
     }

        return records
        .filter(record => record?.id && record?.timestamp)
        .map(record => ({
          id: record.id,
          timeStamp: record.timestamp,
          employeeId: record.employeeId,
          firstName: record.user?.firstName,
          lastName: record.user?.lastName,
          employeeName: record.user
            ? `${record.user.firstName} ${record.user.lastName}`
            : `ID: ${record.employeeId}`,
          email: record.user?.email,
          isTrainDriver: record.user?.isTrainDriver,
          department: record.user?.department,
          breathAlcoholConcentration: record.breathAlcoholConcentration,
          passed: record.passed,
          displayResult: record.passed ? 'pass' as const : 'fail' as const,
          resultText: record.passed ? 'Godkänt' : 'Ej godkänt',
          resultIcon: record.passed ? '✓' : '✗'
      }))

       .sort((a, b) => {

         const timeA = new Date(a.timeStamp).getTime();
         const timeB = new Date(b.timeStamp).getTime();

         if (timeA === timeB) {
           return b.id.localeCompare(a.id);
         }

         return timeB - timeA;
       });

   }

  private filterByTimeRange(records: AlcolockDisplayModel[], hours: number): AlcolockDisplayModel[] {
    const now = new Date();
    const cutoffTime = new Date(now.getTime() - hours * 60 * 60 * 1000);

    return records.filter(record => new Date(record.timeStamp) >= cutoffTime);
  }


  private handleError(error: any): Observable<never> {
    console.error('Alcolock API error:', error);
    return throwError(() => error);
  }



}
